// Whether a user's sites are being served, and for how long on the free plan.
//
// This is the client mirror of the serve-side gate, so the dashboards can say
// what visitors are about to see. Keep the three in step:
//   - api/_lib/siteGate.js   computeAccessState (HTML sites, the real gate)
//   - DynamicPage.js         Figma-embed sites on the default address
//   - DynamicPage2.js        Figma-embed sites on a custom domain
//   - this file              the label the owner sees in the dashboards
// siteGate.parity.test.mjs (run with node --test) checks this file and
// api/_lib/siteGate.js agree on a table of cases.
//
// A planOverride is a real entitlement (functions/index.js setUserPlanOverride),
// so a paid tier keeps the site online. A 'free' override is ignored here: it
// limits the app's features, but it must never take a paying customer's site
// offline.
//
// paid     = active, trialing or past_due subscription, any one-time payment, or
//            an admin planOverride on a paid tier (basic, solo, pro)
// expired  = not paid, and (trialUsed, or 7 days since the trial start)
// trial    = anything else (free, inside the 7 days)
// The trial start is firstProjectDate, else signUpDate (same precedence as the
// server). Without a start date the user counts as inside the trial.

export const TRIAL_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export const PAID_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'];
export const PAID_OVERRIDE_PLANS = ['basic', 'solo', 'pro'];

// A date as milliseconds, whatever shape it was stored in: an ISO string (what
// every writer here uses), a Date, or a Firestore Timestamp (toDate() or
// { seconds }). NaN when it is missing or not a date.
export function toMillis(value) {
  if (!value) return NaN;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'object' && typeof value.seconds === 'number') return value.seconds * 1000;
  return new Date(value).getTime();
}

/**
 * When the free plan's 7 days started: the first project, else sign-up. Every
 * place that counts the trial must use this (or the same two fields), never
 * signUpDate alone, or the pages disagree and the cleanup job deletes data early.
 */
export function trialStartOf(user) {
  return (user && (user.firstProjectDate || user.signUpDate)) || null;
}

/** True for an admin override that grants a paid tier. Accepts { plan } or the plan string. */
export function isPaidOverride(planOverride) {
  const plan = planOverride && typeof planOverride === 'object' ? planOverride.plan : planOverride;
  return typeof plan === 'string' && PAID_OVERRIDE_PLANS.includes(plan.toLowerCase());
}

/**
 * @param {object} input
 * @param {string|null} input.subscriptionStatus latest subscription status, if any
 * @param {boolean} input.hasPayment any one-time payment on file
 * @param {object|string|null} input.planOverride user.planOverride ({ plan } or the plan key)
 * @param {boolean} input.trialUsed user.trialUsed
 * @param {string|number|Date|null} input.trialStart firstProjectDate || signUpDate
 * @param {number} [input.now] injectable clock, for tests
 * @returns {{ state: 'paid'|'trial'|'expired', daysLeft: number|null }}
 *   daysLeft is whole days of free access left (7 on the first day, 1 on the
 *   last) while in 'trial' and the start date is known; null otherwise.
 */
export function computeSiteAccess({
  subscriptionStatus = null,
  hasPayment = false,
  planOverride = null,
  trialUsed = false,
  trialStart = null,
  now = Date.now(),
} = {}) {
  if (PAID_SUBSCRIPTION_STATUSES.includes(subscriptionStatus) || hasPayment || isPaidOverride(planOverride)) {
    return { state: 'paid', daysLeft: null };
  }
  if (trialUsed === true) return { state: 'expired', daysLeft: null };

  const start = toMillis(trialStart);
  if (!Number.isFinite(start)) return { state: 'trial', daysLeft: null };

  const endsAt = start + TRIAL_DAYS * DAY_MS;
  if (now >= endsAt) return { state: 'expired', daysLeft: null };
  // Same count the site's trial note has always shown (DynamicPage.js):
  // 7 minus whole days elapsed, so it reads 7 on the first day and 1 on the last.
  const elapsedDays = Math.max(0, Math.floor((now - start) / DAY_MS));
  return { state: 'trial', daysLeft: Math.max(1, TRIAL_DAYS - elapsedDays) };
}

/** "3 days left", "Last day", for the trial note. */
export function daysLeftLabel(daysLeft) {
  if (daysLeft === null || daysLeft === undefined) return '';
  if (daysLeft <= 0) return 'Last day';
  return daysLeft === 1 ? '1 day left' : `${daysLeft} days left`;
}
